"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslations } from "next-intl"
import { useInfiniteCharactersList } from "@/features/characters/hooks/queries/use-characters"
import { useAuthStore } from "@/features/auth/store"
import { CHARACTER_CATEGORIES } from "@/shared/lib/constants"
import { cn } from "@/shared/lib/utils"
import { CharacterCard } from "@/features/characters/components/character-card"
import { CharacterCardSkeleton } from "@/features/characters/components/character-card-skeleton"

const BOTTOM_PLACED_CHARACTER_ID = "9bb4a4cc-59b9-454a-a16c-794690971a48"
const FEATURED_NEW_CHARACTER_ID = "584dbc8b-acb1-4d56-a385-85fc155b9545"
const NINTH_PLACED_CHARACTER_ID = "3f3a4f3c-db4e-474c-a650-c227e980d796"
const NINTH_PLACED_CHARACTER_INDEX = 7
const SWAPPED_CHARACTER_A_ID = "c8abb383-e076-41c3-bdc9-f510b965bf42"
const SWAPPED_CHARACTER_B_ID = "baf497b4-ec31-488a-9582-495dfcc853a3"
const CHARACTER_BATCH_SIZE = 17
const CHARACTER_PAGE_SIZE = CHARACTER_BATCH_SIZE * 2

function getScrollParent(element: HTMLElement): HTMLElement | Window {
  let parent = element.parentElement

  while (parent) {
    const style = window.getComputedStyle(parent)
    if (/(auto|scroll)/.test(style.overflowY) && parent.scrollHeight > parent.clientHeight) {
      return parent
    }
    parent = parent.parentElement
  }

  return window
}

function getViewportBottom(scroller: HTMLElement | Window): number {
  if (scroller === window) return window.innerHeight
  return (scroller as HTMLElement).getBoundingClientRect().bottom
}

export function LandingCharacterSection() {
  const t = useTranslations("landing")
  // Start from a stable, SSR-safe default so the server and the first client
  // render agree (avoids React #418 hydration mismatch). The user's persisted
  // gender preference is applied after mount, in the effect below.
  const [activeCategory, setActiveCategory] = useState("all")
  const [visibleCount, setVisibleCount] = useState(CHARACTER_BATCH_SIZE)
  const loadMoreRef = useRef<HTMLDivElement | null>(null)

  const preferredGender = useAuthStore((s) => s.profile?.preferredCharacterGender)
  const userSelectedRef = useRef(false)

  function selectCategory(value: string) {
    userSelectedRef.current = true
    setActiveCategory(value)
  }

  // Apply the persisted preference once the profile loads, unless the user has
  // already picked a category. Runs post-hydration, so it never diverges from
  // the server-rendered "All Characters" default.
  useEffect(() => {
    if (userSelectedRef.current) return
    if (preferredGender === "female") setActiveCategory("female")
  }, [preferredGender])

  const {
    data: characterPages,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteCharactersList({
    gender: activeCategory === "anime" ? "all" : (activeCategory as "all" | "female" | "male" | "trans"),
    style: activeCategory === "anime" ? "anime" : undefined,
    search: "",
    official: true,
  }, CHARACTER_PAGE_SIZE)
  const filteredCharacters = useMemo(
    () => characterPages?.pages.flat() ?? [],
    [characterPages]
  )
  const displayCharacters = useMemo(() => {
    const featuredCharacter = filteredCharacters.find(
      (character) => character.id === FEATURED_NEW_CHARACTER_ID
    )
    const bottomCharacter = filteredCharacters.find(
      (character) => character.id === BOTTOM_PLACED_CHARACTER_ID
    )
    const ninthCharacter = filteredCharacters.find(
      (character) => character.id === NINTH_PLACED_CHARACTER_ID
    )
    const remainingCharacters = filteredCharacters.filter(
      (character) =>
        character.id !== FEATURED_NEW_CHARACTER_ID &&
        character.id !== BOTTOM_PLACED_CHARACTER_ID &&
        character.id !== NINTH_PLACED_CHARACTER_ID
    )
    const orderedCharacters = [
      ...(featuredCharacter ? [featuredCharacter] : []),
      ...remainingCharacters,
    ]

    if (ninthCharacter) {
      orderedCharacters.splice(NINTH_PLACED_CHARACTER_INDEX, 0, ninthCharacter)
    }

    const displayCharacters = [
      ...orderedCharacters,
      ...(bottomCharacter ? [bottomCharacter] : []),
    ]

    const firstSwapIndex = displayCharacters.findIndex(
      (character) => character.id === SWAPPED_CHARACTER_A_ID
    )
    const secondSwapIndex = displayCharacters.findIndex(
      (character) => character.id === SWAPPED_CHARACTER_B_ID
    )

    if (firstSwapIndex >= 0 && secondSwapIndex >= 0) {
      const firstCharacter = displayCharacters[firstSwapIndex]
      displayCharacters[firstSwapIndex] = displayCharacters[secondSwapIndex]
      displayCharacters[secondSwapIndex] = firstCharacter
    }

    return displayCharacters
  }, [filteredCharacters])
  const visibleCharacters = useMemo(
    () => displayCharacters.slice(0, visibleCount),
    [displayCharacters, visibleCount]
  )
  const hasMoreCharacters =
    visibleCharacters.length < displayCharacters.length || Boolean(hasNextPage)

  useEffect(() => {
    setVisibleCount(CHARACTER_BATCH_SIZE)
  }, [activeCategory])

  useEffect(() => {
    const target = loadMoreRef.current
    if (!target || !hasMoreCharacters) return

    const scroller = getScrollParent(target)
    const loadNextBatch = () => {
      const targetTop = target.getBoundingClientRect().top
      if (targetTop > getViewportBottom(scroller) + 500) return

      if (visibleCount < displayCharacters.length) {
        setVisibleCount((count) =>
          Math.min(count + CHARACTER_BATCH_SIZE, displayCharacters.length)
        )
        return
      }

      if (hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    }

    scroller.addEventListener("scroll", loadNextBatch, { passive: true })
    window.addEventListener("resize", loadNextBatch)

    return () => {
      scroller.removeEventListener("scroll", loadNextBatch)
      window.removeEventListener("resize", loadNextBatch)
    }
  }, [
    displayCharacters.length,
    fetchNextPage,
    hasMoreCharacters,
    hasNextPage,
    isFetchingNextPage,
    visibleCount,
  ])

  function categoryLabel(value: string): string {
    if (value === "all") return t("categoryLabels.all")
    if (value === "female") return t("categoryLabels.female")
    if (value === "anime") return t("categoryLabels.anime")
    return t("charactersFallback")
  }

  return (
    <section
      className="mx-auto w-full max-w-7xl px-4 pt-6 pb-12 sm:pb-16 sm:px-6 lg:px-8"
      aria-labelledby="characters-heading"
    >
      <div>
        <div className="mb-4 sm:mb-5">
          <h2
            id="characters-heading"
            className="scroll-mt-16 heading-serif text-lg sm:text-xl md:text-2xl"
          >
            {activeCategory === "all"
              ? t("allCharacters")
              : categoryLabel(activeCategory)}
          </h2>
        </div>

        <div
          className="mb-5 sm:mb-6 flex flex-wrap items-center gap-2"
          role="group"
          aria-label={t("filterByCategory")}
        >
          {CHARACTER_CATEGORIES.map((category) => {
            const isActive = activeCategory === category.value
            const label = categoryLabel(category.value)
            return (
              <button
                key={category.value}
                type="button"
                onClick={() => selectCategory(category.value)}
                aria-pressed={isActive}
                aria-label={t("filterBy", { category: label })}
                className={cn(
                  "relative shrink-0 rounded-full px-4 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-sm font-medium transition-colors",
                  isActive
                    ? "text-black"
                    : "border border-border bg-card text-muted-foreground hover:text-foreground hover:border-border/80"
                )}
              >
                {isActive && (
                  <div className="absolute inset-0 rounded-full bg-white" />
                )}
                <span className="relative z-10">{label}</span>
              </button>
            )
          })}
        </div>

        {isLoading ? (
          <div
            className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
            role="status"
            aria-live="polite"
            aria-label={t("loadingCharacters")}
          >
            {Array.from({ length: 10 }).map((_, i) => (
              <CharacterCardSkeleton key={i} variant="default" />
            ))}
          </div>
        ) : visibleCharacters.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center py-16 sm:py-20 text-center px-4"
            role="status"
            aria-live="polite"
          >
            <p className="text-base sm:text-lg font-medium text-muted-foreground">
              {t("noCharacters")}
            </p>
            <p className="mt-1 text-xs sm:text-sm text-muted-foreground/70">
              {t("adjustFilters")}
            </p>
          </div>
        ) : (
          <div
            key={activeCategory}
            className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
            role="list"
            aria-label={t("charactersGrid")}
          >
            {visibleCharacters.map((character, index) => (
              <div key={character.id} role="listitem">
                <CharacterCard
                  character={character}
                  priority={index < 2}
                  sticker={character.id === FEATURED_NEW_CHARACTER_ID ? "New" : undefined}
                />
              </div>
            ))}
            {hasMoreCharacters && (
              <div ref={loadMoreRef} className="col-span-full h-px" aria-hidden="true" />
            )}
          </div>
        )}
      </div>
    </section>
  )
}
